import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import OrgSwitcher from "./OrgSwitcher";
import "./Navbar.css";

function Dropdown({ trigger, children, align = "right", label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div className="dropdown" ref={ref}>
      <button
        className="dropdown-trigger"
        onClick={() => setOpen((o) => !o)}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label || undefined}
      >
        {trigger}
      </button>
      {open && (
        <div
          className={`dropdown-menu dropdown-menu--${align}`}
          role="menu"
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("navbar");

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <nav className="navbar" aria-label={t("primaryNav")}>
      <Link to={user ? "/dashboard" : "/"} className="navbar-brand">
        <Logo size={20} className="navbar-logo-icon" />
        <span className="navbar-logo-text">pikra</span>
      </Link>

      {user ? <OrgSwitcher /> : null}

      <div className="navbar-center">
        <Link to="/dashboard">{t("home")}</Link>
        <Link to="/tickets">{t("tickets")}</Link>
        <Link to="/validate">{t("validate")}</Link>
      </div>

      <div className="navbar-right">
        <LanguageSwitcher className="navbar-lang" />
        {user ? (
          <>
            <Dropdown
              label={t("createMenu")}
              trigger={<><span className="dropdown-plus" aria-hidden="true">+</span> {t("create")}</>}
            >
              <Link to="/create" className="dropdown-item" role="menuitem">{t("newEvent")}</Link>
            </Dropdown>

            <Dropdown
              label={t("userMenu")}
              trigger={
                <span className="navbar-avatar-trigger">
                  <span className="navbar-avatar-circle" aria-hidden="true">
                    {(profile?.username || "U")[0].toUpperCase()}
                  </span>
                  <span className="navbar-avatar-name">{profile?.username}</span>
                  <span className="dropdown-caret" aria-hidden="true">&#9662;</span>
                </span>
              }
            >
              <Link to="/settings" className="dropdown-item" role="menuitem">{t("settings")}</Link>
              <div className="dropdown-divider" role="separator" />
              <button onClick={handleSignOut} role="menuitem" className="dropdown-item dropdown-item--danger">
                {t("logout")}
              </button>
            </Dropdown>
          </>
        ) : (
          <>
            <Link to="/login" className="navbar-link">{t("login")}</Link>
            <Link to="/create" className="navbar-btn-primary">{t("createEvent")}</Link>
          </>
        )}
      </div>
    </nav>
  );
}
